/**
 * Resolução das imagens, junto do tamanho do download na barra de tempo.
 *
 * - `ResolutionChip` (desktop): botão discreto com a largura exibida ("3,6k")
 *   e o modo ("AUTO"/"FIXA"); abre um menu para cima com "Automática" e as
 *   larguras fixas.
 * - `ResolutionSelect` (celular): o mesmo ajuste num seletor, dentro do
 *   diálogo de opções da animação.
 * - `useResolutionLabel`: texto curto ("1,8k") para a barra do celular.
 *
 * Tudo usa o MESMO ajuste de qualidade das configurações e do link
 * (`useLoopStore.quality`), então a automática continua sendo o padrão.
 */
import clsx from 'clsx';
import { Check, LoaderCircle } from 'lucide-react';
import { formatBytes } from '../lib/format';
import { useEffect, useId, useRef, useState } from 'react';
import { QUALITY_LABELS, QUALITY_MODES, type QualityMode } from '../goes/budget';
import { usePlanStore } from '../loop/plan';
import { useDetalheStore } from '../detalhe/estado';
import { useLoopStore } from '../loop/store';
import { useModalLayer } from './modalStack';
import { Select, type SelectOption } from './primitives';

/** Rótulo curto da largura: 900 → "900", 3600 → "3,6k". */
export function shortWidth(w: number): string {
  return w >= 1000 ? `${String(w / 1000).replace('.', ',')}k` : String(w);
}

/**
 * Largura na tela agora. Parada: o quadro no tamanho do arquivo (ou a versão
 * mais nítida, se houver). Tocando: a do BITMAP da animação, que pode ser
 * menor que o arquivo (o quadro é decodificado no tamanho que a tela mostra)
 * — dizer "7200" desenhando 5400 fazia a qualidade parecer quebrada. Com o
 * detalhe em blocos, a nitidez da área visível é a do nível dele.
 */
function useShownWidth(): { shown: number | null; file: number | null; downloading: number | null } {
  const planWidth = usePlanStore((s) => s.plan?.width ?? null);
  const playWidth = usePlanStore((s) => s.playWidth);
  const stillWidth = usePlanStore((s) => s.stillWidth);
  const bitmapWidth = usePlanStore((s) => s.bitmapWidth);
  const playing = useLoopStore((s) => s.playing);
  const loopWidth = playWidth ?? planWidth;
  const file = !playing && stillWidth && loopWidth && stillWidth > loopWidth ? stillWidth : loopWidth;
  const nivel = useDetalheStore((s) => s.nivel);
  const inteiro = playing && bitmapWidth && file && bitmapWidth < file ? bitmapWidth : file;
  const shown = nivel && (inteiro === null || nivel > inteiro) ? nivel : inteiro;
  // Trocando de qualidade: a nova largura ainda está baixando.
  const downloading = planWidth !== null && playWidth !== null && planWidth !== playWidth ? planWidth : null;
  return { shown, file, downloading };
}

/** "1,8k" (ou "1,8k auto") para textos curtos; null antes de haver imagens. */
export function useResolutionLabel(): string | null {
  const quality = useLoopStore((s) => s.quality);
  const { shown } = useShownWidth();
  if (shown === null) return null;
  return quality === 'auto' ? `${shortWidth(shown)} auto` : `${shortWidth(shown)} fixa`;
}

function summaryOf(shown: number, quality: QualityMode): string {
  return `Resolução das imagens: ${shown} px${quality === 'auto' ? ', automática (acompanha o zoom)' : `, fixa (${QUALITY_LABELS[quality]})`}`;
}

/** Botão da resolução (desktop). Mostra também o tamanho estimado do download, que é o que a resolução muda. */
export function ResolutionChip() {
  const quality = useLoopStore((s) => s.quality);
  const hasPlan = usePlanStore((s) => s.plan !== null);
  // Com o detalhe em blocos: o que já veio deles mais a estimativa do que falta (tamanho medido).
  const blocos = useDetalheStore((s) => s.progresso);
  const bytes = usePlanStore((s) => s.plan?.bytesTotal ?? 0) + blocos.bytes + blocos.faltam;
  const { shown, file, downloading } = useShownWidth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Escape fecha (camada não modal: o mapa continua usável).
  useModalLayer(open, { onEscape: () => setOpen(false), modal: false, ref: menuRef });

  // Clique fora fecha.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  if (!hasPlan || shown === null) return null;

  const auto = quality === 'auto';
  const choose = (q: QualityMode) => {
    useLoopStore.getState().setSettings({ quality: q });
    setOpen(false);
  };
  const summary = summaryOf(shown, quality);
  const title = bytes ? `${summary}. Download estimado desta animação: ${formatBytes(bytes)}` : summary;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${summary}. Mudar resolução`}
        title={title}
        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-ink-600 bg-ink-800 px-2 font-mono text-[11px] text-ink-100 tabular-nums hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
      >
        {shortWidth(shown)}
        {downloading !== null && <LoaderCircle className="size-3 animate-spin text-mata-300" aria-hidden />}
        <span
          className={clsx(
            'rounded px-1 font-sans text-[10px] leading-4 font-medium',
            auto ? 'bg-mata-500/20 text-mata-300' : 'bg-ink-700 text-ink-300',
          )}
        >
          {auto ? 'auto' : 'fixa'}
        </span>
        {bytes > 0 && <span className="text-ink-300">· {formatBytes(bytes)}</span>}
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Resolução das imagens"
          className="absolute right-0 bottom-full z-30 mb-2 w-60 overflow-hidden rounded-xl border border-ink-600/90 bg-ink-900 py-1 text-sm shadow-2xl"
        >
          <p className="px-3 pt-1.5 pb-1 text-[11px] text-ink-400">
            Na tela: <span className="font-mono text-ink-200 tabular-nums">{shown} px</span>
            {file !== null && file > shown && (
              <>
                {' '}
                de <span className="font-mono tabular-nums">{file} px</span>
              </>
            )}
            {downloading !== null && (
              <span className="text-mata-300">
                {' '}
                · baixando <span className="font-mono tabular-nums">{downloading} px</span>
              </span>
            )}
          </p>
          {QUALITY_MODES.map((q) => {
            const selected = q === quality;
            return (
              <button
                key={String(q)}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => choose(q)}
                className={clsx(
                  'flex min-h-9 w-full items-center gap-2 px-3 text-left hover:bg-ink-700/70 focus-visible:bg-ink-700/70 focus-visible:outline-none',
                  selected ? 'text-white' : 'text-ink-200',
                )}
              >
                <Check className={clsx('size-4 shrink-0', selected ? 'text-mata-400' : 'invisible')} aria-hidden />
                <span className="flex-1">
                  {q === 'auto' ? (
                    <>
                      Automática <span className="text-[11px] text-ink-400">(acompanha o zoom)</span>
                    </>
                  ) : (
                    QUALITY_LABELS[q]
                  )}
                </span>
                {q !== 'auto' && <span className="font-mono text-[11px] text-ink-400 tabular-nums">{q} px</span>}
              </button>
            );
          })}
          <p className="border-t border-ink-700/70 px-3 pt-1.5 pb-1 text-[11px] leading-snug text-ink-400">
            Automática: equilibra nitidez e processador. Fixa: a nitidez inteira que a tela mostra, também na
            animação; pesa mais no processador e nos dados.
          </p>
        </div>
      )}
    </div>
  );
}

const SELECT_OPTIONS: SelectOption<string>[] = QUALITY_MODES.map((q) => ({
  value: String(q),
  label: q === 'auto' ? 'Automática (acompanha o zoom)' : `${QUALITY_LABELS[q]} — ${q} px`,
}));

/** Seletor da resolução (celular, no diálogo de opções da animação). */
export function ResolutionSelect() {
  const quality = useLoopStore((s) => s.quality);
  const { shown } = useShownWidth();
  return (
    <div className="space-y-1">
      <Select
        label="Resolução das imagens"
        value={String(quality)}
        options={SELECT_OPTIONS}
        onChange={(v) => useLoopStore.getState().setSettings({ quality: v === 'auto' ? 'auto' : (Number(v) as QualityMode) })}
      />
      {shown !== null && <p className="text-[11px] text-ink-400 tabular-nums">Na tela agora: {shown} px</p>}
    </div>
  );
}
